import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import authService, { User, LoginCredentials, RegisterData } from '../services/auth.service';
import { logger } from '@/lib/logger';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  // Issue #14: Helper functions para verificar permisos
  canAccessCliente: (clienteId: number) => boolean;
  canAccessMarca: (marca: string) => boolean;
  // RBAC: helper para módulos
  canAccessModulo: (moduloCodigo: string, accion?: string) => boolean;
  // Impersonation (admin → "ver como" otro usuario para QA)
  impersonate: (targetUserId: number) => Promise<void>;
  stopImpersonate: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

// Cleanup one-time de claves legacy que se creaban cuando el auth vivía
// en localStorage. La app ya usa cookie HttpOnly — esto evita que un
// token viejo siga “visible” desde el devtools del navegador.
try {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('user');
    window.localStorage.removeItem('auth-storage');
  }
} catch { /* localStorage disabled */ }

/**
 * 2026-04-23: el user ya NO se persiste en localStorage. La fuente de
 * verdad es `/api/auth/check` (cookie HttpOnly). En el bootstrap de App
 * se llama `checkAuth()` una única vez y llena el store en memoria.
 */
const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // Estado inicial - isLoading true hasta verificar auth
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      // Acciones
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authService.login(credentials);

          if (response.success && response.user) {
            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            throw new Error(response.message || 'Error al iniciar sesión');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Error al iniciar sesión';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          set({ isLoading: true });
          await authService.logout();
        } catch (error) {
          logger.error('Error al cerrar sesión:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authService.register(data);

          if (response.success && response.user) {
            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } else {
            throw new Error(response.message || 'Error al registrar usuario');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Error al registrar usuario';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true });

          const user = await authService.checkAuth();

          if (user) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          logger.error('Error al verificar autenticación:', error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      // Issue #14: Helper function para verificar acceso a cliente
      canAccessCliente: (clienteId: number): boolean => {
        const { user } = get();
        if (!user) return false;

        // Admin siempre tiene acceso
        if (user.rol === 'admin') return true;

        // Si tiene todos los clientes asignados
        if (user.tiene_todos_clientes || user.permisos?.tiene_todos_clientes) {
          return true;
        }

        // Verificar en la lista de clientes asignados
        if (user.permisos?.clientes) {
          return user.permisos.clientes.includes(clienteId);
        }

        return false;
      },

      // RBAC: verifica acceso a un módulo+acción
      canAccessModulo: (moduloCodigo: string, accion: string = 'ver'): boolean => {
        const { user } = get();
        if (!user) return false;
        // Admin: bypass total (es_admin=true o legacy rol='admin')
        if (user.es_admin || user.rol === 'admin') return true;
        const permisos = user.permisos_modulos;
        if (!permisos || permisos.length === 0) return false;
        if (permisos.includes('*')) return true;
        return permisos.includes(`${moduloCodigo}:${accion}`);
      },

      // RBAC: impersonar a otro usuario
      impersonate: async (targetUserId: number) => {
        try {
          set({ isLoading: true });
          const user = await authService.impersonate(targetUserId);
          if (user) {
            set({ user, isAuthenticated: true, isLoading: false, error: null });
          } else {
            throw new Error('No se pudo impersonar al usuario');
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // RBAC: detener impersonation
      stopImpersonate: async () => {
        try {
          set({ isLoading: true });
          const user = await authService.stopImpersonate();
          if (user) {
            set({ user, isAuthenticated: true, isLoading: false, error: null });
          } else {
            throw new Error('No se pudo restaurar la sesión');
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Issue #14: Helper function para verificar acceso a marca
      canAccessMarca: (marca: string): boolean => {
        const { user } = get();
        if (!user) return false;

        // Admin siempre tiene acceso
        if (user.rol === 'admin') return true;

        // Si tiene todas las marcas asignadas
        if (user.tiene_todas_marcas || user.permisos?.tiene_todas_marcas) {
          return true;
        }

        // Verificar en la lista de marcas asignadas (case-insensitive)
        if (user.permisos?.marcas) {
          const marcaLower = marca.toLowerCase();
          return user.permisos.marcas.some(m => m.toLowerCase() === marcaLower);
        }

        return false;
      },
    }),
    { name: 'AuthStore' }
  )
);

export default useAuthStore;
